import type { Dispatch, SetStateAction } from 'react';
import {
  BsChevronLeft,
  BsChevronRight,
  BsChevronDoubleLeft,
  BsChevronDoubleRight,
} from 'react-icons/bs';

interface CustomPageSizeProps {
  label: string;
  value: number;
}
interface Props {
  metaData: any;
  pageNumber: number;
  setPageNumber: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  loading?: boolean;
  fetchData?: (pageNumber: number, pageSize: number) => void;
  customPageSize?: CustomPageSizeProps[];
}

const CustomPagination = ({
  metaData,
  pageNumber,
  setPageNumber,
  pageSize,
  setPageSize,
  loading,
  fetchData,
  customPageSize,
}: Props) => {
  const currentPage = metaData?.current_page || 1;
  const totalPages = metaData?.total_pages || 1;

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 3; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const fetchCustomData = (pageNumber: number, pageSize: number) => {
    fetchData && fetchData(pageNumber, pageSize);
  };

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-4">
      {/* First page button */}
      <button
        onClick={() => {
          setPageNumber(1);
          fetchCustomData(1, pageSize);
        }}
        disabled={currentPage === 1 || loading}
        className="tw-cursor-pointer tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-green-400 tw-text-gray-600 hover:tw-border-blue-500 hover:tw-text-blue-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-300 disabled:hover:tw-text-gray-600 tw-transition-colors"
        title="First page"
      >
        <BsChevronDoubleLeft className="tw-text-base" />
      </button>

      {/* Previous button */}
      <button
        onClick={() => {
          if (metaData?.has_previous) {
            setPageNumber(pageNumber - 1);
            fetchCustomData(pageNumber - 1, pageSize);
          }
        }}
        disabled={!metaData?.has_previous || loading}
        className="tw-cursor-pointer tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-green-400 tw-text-gray-600 hover:tw-border-blue-500 hover:tw-text-blue-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-300 disabled:hover:tw-text-gray-600 tw-transition-colors"
        title="Previous page"
      >
        <BsChevronLeft className="tw-text-base" />
      </button>

      {/* Page numbers */}
      <div className="tw-flex tw-items-center tw-gap-1">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="tw-min-w-[32px] tw-h-8 tw-px-2 tw-flex tw-items-center tw-justify-center tw-text-gray-500"
              >
                ...
              </span>
            );
          }

          const isActive = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => {
                setPageNumber(page as number);
                fetchCustomData(page as number, pageSize);
              }}
              disabled={loading}
              className={`tw-min-w-[32px] tw-h-8 tw-px-2 tw-flex tw-items-center tw-justify-center tw-rounded  tw-transition-colors tw-text-sm tw-font-medium ${
                isActive
                  ? 'tw-border tw-bg-green-500 tw-text-white tw-border-green-600'
                  : 'tw-border-none tw-bg-white tw-text-gray-700  hover:tw-border-blue-500 hover:tw-text-blue-600'
              } disabled:tw-opacity-40 disabled:tw-cursor-not-allowed`}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* Next button */}
      <button
        onClick={() => {
          if (metaData?.has_next) {
            setPageNumber(pageNumber + 1);
            fetchCustomData(pageNumber + 1, pageSize);
          }
        }}
        disabled={!metaData?.has_next || loading}
        className="tw-cursor-pointer tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-green-400 tw-text-gray-600 hover:tw-border-blue-500 hover:tw-text-blue-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-300 disabled:hover:tw-text-gray-600 tw-transition-colors"
        title="Next page"
      >
        <BsChevronRight className="tw-text-base" />
      </button>

      {/* Last page button */}
      <button
        onClick={() => {
          setPageNumber(totalPages);
          fetchCustomData(totalPages, pageSize);
        }}
        disabled={currentPage === totalPages || loading}
        className="tw-cursor-pointer tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-green-400 tw-text-gray-600 hover:tw-border-blue-500 hover:tw-text-blue-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-border-gray-300 disabled:hover:tw-text-gray-600 tw-transition-colors"
        title="Last page"
      >
        <BsChevronDoubleRight className="tw-text-base" />
      </button>

      {/* Page size selector */}
      <select
        value={pageSize}
        onChange={(e) => {
          const newSize = parseInt(e.target.value);
          setPageSize(newSize);
          setPageNumber(1);
          fetchCustomData(1, newSize);
        }}
        className="tw-h-8 tw-px-3 tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 tw-text-sm tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-ml-4"
      >
        {/* <option value="10">10 / page</option> */}
        <option value="20">20 / page</option>
        <option value="50">50 / page</option>
        <option value="100">100 / page</option>
        {customPageSize?.length > 0 &&
          customPageSize?.map((item: CustomPageSizeProps) => (
            <option key={item?.value}>{item?.label}</option>
          ))}
      </select>
    </div>
  );
};

export default CustomPagination;
